import {showNewNotification} from '@edna/components';

import {TData, TPayload} from 'src/components/List';
import {TItemId} from 'src/constants';
import {getSelfRegistrationFilter} from 'src/containers/LeadCompanyClients/utils';
import {CACHE_LIFETIME, ECacheTag, listenerMiddleware, rootApi} from 'src/models';
import {booleanHashToArray} from 'src/utils';

import {
  EBillingStatus,
  ESolutionIdApproveStatus,
  TFilters,
  TRegisterSolutionIdResponse,
  TSupportClient,
} from './definitions';

const getBillingFilter = (billingStatus?: Record<EBillingStatus, boolean>) => {
  if (billingStatus?.OFF && !billingStatus?.ON) {
    return true;
  }

  if (billingStatus?.ON && !billingStatus?.OFF) {
    return false;
  }

  return null;
};

const prepareFilters = (payload: TFilters) => {
  const {
    period,
    registrationType,
    licenseStatus,
    tenantStatus,
    documentType,
    billingStatus,
    channels,
    ...other
  } = payload;

  return {
    ...other,
    createdAtTo: period.stopDate,
    createdAtFrom: period.startDate,
    licenseStatus: booleanHashToArray(licenseStatus),
    tenantStatus: booleanHashToArray(tenantStatus),
    documentType: booleanHashToArray(documentType),
    channels: booleanHashToArray(channels),
    isBillingDisabled: getBillingFilter(billingStatus),
    selfRegistered: getSelfRegistrationFilter(registrationType),
  };
};

const api = rootApi.injectEndpoints({
  overrideExisting: 'throw',
  endpoints: (builder) => ({
    getSupportClients: builder.query<TData<TSupportClient>, TPayload<TFilters>>({
      query: (data) => ({
        method: 'POST',
        url: `/tenantmanagement/tenant/list`,
        data: prepareFilters(data),
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: (result) => {
        if (!result) {
          return [ECacheTag.SUPPORT_CLIENTS];
        }

        return [
          ECacheTag.SUPPORT_CLIENTS,
          ...result.content.map(({id}) => ({id, type: ECacheTag.SUPPORT_CLIENTS})),
        ];
      },
    }),
    getSupportClient: builder.query<TSupportClient, TItemId>({
      query: (id) => ({
        method: 'GET',
        url: `/tenantmanagement/tenant/${id}/data`,
        meta: {isShowError: true, errorMessageKey: 'List:errors.failedToLoadItem'},
      }),
      providesTags: (_, __, id) => [{id, type: ECacheTag.SUPPORT_CLIENTS}],
    }),
    blockSupportClient: builder.mutation<void, {id: TItemId; startAt: Date; endAt: Date}>({
      query: (data) => ({
        method: 'POST',
        url: `/tenantmanagement/tenant/${data.id}/temporary-block`,
        data,
        meta: {
          isShowError: true,
          isShowSuccess: true,
          successMessageKey: 'ClientStatus:notifications.successBlock',
        },
      }),
      invalidatesTags: (_, __, data) => [{type: ECacheTag.SUPPORT_CLIENTS, id: data.id}],
    }),
    unblockSupportClient: builder.mutation<void, TItemId>({
      query: (id) => ({
        method: 'POST',
        url: `/tenantmanagement/tenant/${id}/temporary-block/running/unblock`,
        meta: {
          isShowError: true,
          isShowSuccess: true,
          successMessageKey: 'ClientStatus:notifications.successUnblock',
        },
      }),
      invalidatesTags: (_, __, id) => [{type: ECacheTag.SUPPORT_CLIENTS, id}],
    }),

    getSolutionIdByTenantId: builder.query<TRegisterSolutionIdResponse, TItemId>({
      query: (tenantId) => ({
        method: 'GET',
        url: '/channel-profile/whatsapp/solution-id-registration',
        params: {tenantId},
      }),
      providesTags: (_, __, tenantId) => [{id: tenantId, type: ECacheTag.SOLUTION_ID}],
    }),
    registerSolutionId: builder.mutation<
      TRegisterSolutionIdResponse,
      {tenantId: string; solutionId: string; name: string}
    >({
      query: (data) => ({
        method: 'POST',
        url: '/channel-profile/whatsapp/solution-id-registration',
        data,
      }),
      invalidatesTags: (_, __, {tenantId}) => [{id: tenantId, type: ECacheTag.SOLUTION_ID}],
    }),
  }),
});

listenerMiddleware.startListening({
  matcher: api.endpoints.registerSolutionId.matchFulfilled,
  effect: ({payload}) => {
    if (payload.approveStatus === ESolutionIdApproveStatus.DONE) {
      showNewNotification({
        type: 'SUCCESS',
        messageKey: 'List:notification.saveSuccess',
      });
    }
  },
});

export const {
  useGetSupportClientsQuery,
  useGetSupportClientQuery,
  useBlockSupportClientMutation,
  useUnblockSupportClientMutation,
  useGetSolutionIdByTenantIdQuery,
  useRegisterSolutionIdMutation,
  endpoints: supportsEndpoints,
} = api;
